import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  BuildingOffice2Icon,
  Squares2X2Icon,
  ChartBarSquareIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import CompanyManagement from '../components/Organization/CompanyManagement';
import DepartmentManagement from '../components/Organization/DepartmentManagement';
import DivisionManagement from '../components/Organization/DivisionManagement';
import OrganizationHierarchy from '../components/Organization/OrganizationHierarchy';
import OrganizationRoleManagement from '../components/Organization/OrganizationRoleManagement';
import { useAuth } from '../contexts/AuthContext';

const Organization = () => {
  const location = useLocation();
  const { user, isAdmin, isManager } = useAuth();
  
  const navigationItems = [
    {
      name: 'Company Structure',
      href: '/organization/companies',
      icon: BuildingOfficeIcon,
      description: 'Manage company information, settings, and multi-company setup',
      roles: ['admin', 'hr_manager']
    },
    {
      name: 'Departments',
      href: '/organization/departments',
      icon: BuildingOffice2Icon,
      description: 'Manage departments, department heads, and organizational units',
      roles: ['admin', 'hr_manager', 'manager']
    },
    {
      name: 'Divisions',
      href: '/organization/divisions',
      icon: Squares2X2Icon,
      description: 'Manage divisions within departments and sub-organizational structures',
      roles: ['admin', 'hr_manager', 'manager']
    },
    {
      name: 'Hierarchy View',
      href: '/organization/hierarchy',
      icon: ChartBarSquareIcon,
      description: 'Visual organizational hierarchy and reporting structure',
      roles: ['admin', 'hr_manager', 'manager']
    },
    {
      name: 'Role Management',
      href: '/organization/roles',
      icon: UserGroupIcon,
      description: 'Configure organizational roles, hierarchy levels, and role assignments',
      roles: ['admin', 'hr_manager']
    }
  ];

  // Filter navigation items based on user role
  const filteredNavigation = navigationItems.filter(
    item => !item.roles || item.roles.includes(user?.role)
  );

  const isCurrentPath = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // If we're at the base organization route, show the overview page
  if (location.pathname === '/organization') {
    return (
      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-8">
            <div className="text-center">
              <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h2 className="mt-2 text-lg font-medium text-gray-900">
                Organizational Management
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Manage your company structure, departments, divisions, and organizational hierarchy
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
          {filteredNavigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg shadow hover:shadow-md transition-shadow"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-primary-50 text-primary-600 ring-4 ring-white">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-900 group-hover:text-primary-600">
                    <span className="absolute inset-0" aria-hidden="true" />
                    {item.name}
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    {item.description}
                  </p>
                </div>
                <span
                  className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                  aria-hidden="true"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="m11.293 17.293 1.414 1.414L19.414 12l-6.707-6.707-1.414 1.414L15.586 11H6v2h9.586l-4.293 4.293z" />
                  </svg>
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Navigation breadcrumb */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <nav className="flex space-x-8" aria-label="Tabs">
            {filteredNavigation.map((item) => {
              const current = isCurrentPath(item.href);
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm
                    ${current
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className={`
                    -ml-0.5 mr-2 h-5 w-5
                    ${current ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content area */}
      <div className="bg-white shadow rounded-lg">
        <Routes>
          <Route path="companies/*" element={<CompanyManagement />} />
          <Route path="departments/*" element={<DepartmentManagement />} />
          <Route path="divisions/*" element={<DivisionManagement />} />
          <Route path="hierarchy/*" element={<OrganizationHierarchy />} />
          <Route path="roles/*" element={<OrganizationRoleManagement />} />
          <Route path="*" element={<Navigate to="/organization" replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default Organization;